import { faCamera, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography, styled } from "@mui/material";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { openPopup } from "../../stateManagement/actions/popupActions";
import {
  excelExtensions,
  fileTypes,
  genericFileUploadTypes,
  imageExtensions,
  pdfExtensions,
} from "./genericFileUploadTypes";
import pdfIcon from "../../images/PDF.png";
import pdfIconNew from "../../assets/images/png/adobeLogo.png";
import pptIcon from "../../images/PPT.png";
import excelIcon from "../../images/XLS.png";
import defaultFileIcon from "../../images/defaultFileIcon.png";
import "./GenericFileUpload.css";
import { uploadImages } from "../../services/bannerServices";
import { Loader } from "../loader/Loader";
import {
  TranslationEnum,
  usePortalTranslation,
} from "../../providers/TranslationProvider";

export const GenericFileUpload = (props: genericFileUploadTypes) => {
  const { translate } = usePortalTranslation();
  const CUR_COMPONENT_COMMON = "commonPortal";
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imageDisplayIcon, setImageDisplayIcon] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [acceptAttribute, setAcceptAttribute] = useState<string>("");
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const RemoveButton = styled("span")(`
    background-color: #227C9D;
    color: white;
    border-radius: 50%;
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    z-index: 10; /* Ensure the button appears above the image */
  `);
  useEffect(() => {
    let finalAcceptAttribute = "";
    for (let fileTypeAttribute in props.accept) {
      const tempArray = props.accept[fileTypeAttribute as fileTypes];
      if (tempArray) {
        for (const fileExtensionType of tempArray) {
          finalAcceptAttribute +=
            fileTypeAttribute + "/" + fileExtensionType + ", ";
        }
      }
    }
    setAcceptAttribute(finalAcceptAttribute);
    if (props.defaultFile && props.defaultFile.length > 0) {
      const splitArray = props.defaultFile.split(".");
      const extension = splitArray[splitArray.length - 1];
      if (imageExtensions.includes(extension)) {
        setImageDisplayIcon(props.defaultFile);
      } else if (pdfExtensions.includes(extension)) {
        setImageDisplayIcon(pdfIcon);
      } else if (excelExtensions.includes(extension)) {
        setImageDisplayIcon(excelIcon);
      } else {
        setImageDisplayIcon(props.defaultFile);
      }
    }
  }, []);

  const validateImageAndUpload = async (file: File | undefined) => {
    if (!file) return;
    const isGif = file.type === "image/gif";
    const maxBytes = isGif
      ? props.maxGifSize
        ? props.maxGifSize * 1024
        : undefined
      : props.maxImageSize
        ? props.maxImageSize * 1024
        : undefined;

    if (maxBytes && file.size > maxBytes) {
      openPopup("Alert", `The image uploaded is larger than ${props.maxImageSize} KB. Please re-upload an image within the allowed size.`);
      return;
    }
    if (file) {
      let fileType = file.type.split("/")[0] as fileTypes;
      let fileExtension = file.type.split("/")[1];
      const temp = props.accept[fileType] as string[];
      let isValidExtension = true;
      if (fileType !== "image" && !temp.includes("*")) {
        isValidExtension = temp.includes(fileExtension);
      }

      if (!isValidExtension) {
        openPopup("Alert", `File with ${fileExtension} extension is not valid`);
        return;
      }
      if (!props.fetchBlobKey) {
        if (file.type === "application/pdf") {
          setImageDisplayIcon(pdfIcon);
          if (props.setFile) props.setFile(file);
          if (props.setDefaultFile) props.setDefaultFile("");
          setFileName(file.name);
        } else if (
          file.type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          file.type === "text/csv" ||
          file.type === "application/vnd.ms-excel"
        ) {
          setImageDisplayIcon(excelIcon);
          if (props.setFile) props.setFile(file);
          if (props.setDefaultFile) props.setDefaultFile("");
          setFileName(file.name);
          if (props.setFileAttributes)
            props.setFileAttributes({
              fileName: file.name,
              fileType: file.type,
            });
        } else if (
          file.type ===
            "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
          file.type === "application/vnd.ms-powerpoint"
        ) {
          setImageDisplayIcon(pptIcon);
          if (props.setFile) props.setFile(file);
          if (props.setDefaultFile) props.setDefaultFile("");
          setFileName(file.name);
          if (props.setFileAttributes)
            props.setFileAttributes({
              fileName: file.name,
              fileType: file.type,
            });
        } else if (fileType === "image") {
          if (props.resolution) {
            let img = new Image();
            let objectUrl = URL.createObjectURL(file);
            img.onload = function (evt) {
              if (
                img.naturalHeight !== props.resolution!.height ||
                img.naturalWidth !== props.resolution!.width
              ) {
                openPopup(
                  "Alert",
                  translate(
                    TranslationEnum.common_portal,
                    "image resolution must be {resolutionWidth}X{resolutionHeight} , your image resolution is  {naturalWidth}X{naturalHeight}",
                    {
                      resolutionWidth: props.resolution!.width.toString(),
                      resolutionHeight: props.resolution!.height.toString(),
                      naturalWidth: img.naturalWidth.toString(),
                      naturalHeight: img.naturalHeight.toString(),
                    }
                  )
                );
              } else {
                if (props.setFile) props.setFile(file);
                if (props.setDefaultFile) props.setDefaultFile("");
                setImageDisplayIcon(objectUrl);
                setFileName(file.name);
                if (props.setFileAttributes)
                  props.setFileAttributes({
                    fileName: file.name,
                    fileType: file.type,
                  });
              }
            };
            img.src = objectUrl;
          } else {
            if (props.setFile) props.setFile(file);
            if (props.setDefaultFile) props.setDefaultFile("");
            setImageDisplayIcon(URL.createObjectURL(file));
            setFileName(file.name);
            if (props.setFileAttributes)
              props.setFileAttributes({
                fileName: file.name,
                fileType: file.type,
              });
          }
        }
      } else {
        // if fetchBlobkey = true
        setImageLoading(true);
        if (fileType === "image") {
          if (props.resolution) {
            let img = new Image();
            let objectUrl = URL.createObjectURL(file);
            img.onload = async function (evt) {
              if (
                img.naturalHeight !== props.resolution!.height ||
                img.naturalWidth !== props.resolution!.width
              ) {
                setImageLoading(false);
                openPopup(
                  "Alert",
                  translate(
                    TranslationEnum.common_portal,
                    "image resolution must be {resolutionWidth}X{resolutionHeight} , your image resolution is  {naturalWidth}X{naturalHeight}",
                    {
                      resolutionWidth: props.resolution!.width.toString(),
                      resolutionHeight: props.resolution!.height.toString(),
                      naturalWidth: img.naturalWidth.toString(),
                      naturalHeight: img.naturalHeight.toString(),
                    }
                  )
                );
              } else {
                const fileData = new FormData();
                fileData.append("files", file);
                const blobUrlResponse = fileData.has("files")
                  ? await uploadImages(fileData)
                  : [];
                if (!Array.isArray(blobUrlResponse)) {
                  setImageLoading(false);
                  openPopup("Error", blobUrlResponse.message);
                  return;
                }
                // props.setFile(file);
                if (props.setDefaultFile)
                  props.setDefaultFile(blobUrlResponse[0]);
                setImageDisplayIcon(blobUrlResponse[0]);
                setImageLoading(false);
                setFileName(file.name);
                if (props.setFileAttributes)
                  props.setFileAttributes({
                    fileName: file.name,
                    fileType: file.type,
                  });
              }
            };
            img.src = objectUrl;
          } else {
            // props.setFile(file);
            // if(props.setDefaultFile) props.setDefaultFile("");
            // setImageDisplayIcon(URL.createObjectURL(file));
            // setFileName(file.name);
            const fileData = new FormData();
            fileData.append("files", file);
            const blobUrlResponse = fileData.has("files")
              ? await uploadImages(fileData)
              : [];
            if (!Array.isArray(blobUrlResponse)) {
              setImageLoading(false);
              openPopup("Error", blobUrlResponse.message);
              return;
            }
            // props.setFile(file);
            if (props.setDefaultFile) props.setDefaultFile(blobUrlResponse[0]);
            setImageDisplayIcon(blobUrlResponse[0]);
            setImageLoading(false);
          }
        } else {
          const fileData = new FormData();
          fileData.append("files", file);
          const blobUrlResponse = fileData.has("files")
            ? await uploadImages(fileData)
            : [];
          if (!Array.isArray(blobUrlResponse)) {
            setImageLoading(false);
            openPopup("Error", blobUrlResponse.message);
            return;
          }
          if (file.type === "application/pdf") {
            setImageDisplayIcon(pdfIcon);
            // props.setFile(file);
          } else if (
            file.type ===
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            file.type === "text/csv" ||
            file.type === "application/vnd.ms-excel"
          ) {
            setImageDisplayIcon(excelIcon);
            // props.setFile(file);
            // if(props.setDefaultFile) props.setDefaultFile("");
            // setFileName(file.name);
          } else if (
            file.type ===
              "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
            file.type === "application/vnd.ms-powerpoint"
          ) {
            setImageDisplayIcon(pptIcon);
            // props.setFile(file);
            // if(props.setDefaultFile) props.setDefaultFile("");
            // setFileName(file.name);
          } else {
            setImageDisplayIcon(blobUrlResponse[0]);
          }
          if (props.setDefaultFile) props.setDefaultFile(blobUrlResponse[0]);
          setFileName(file.name);
          if (props.setFileAttributes)
            props.setFileAttributes({
              fileName: file.name,
              fileType: file.type,
            });
          setImageLoading(false);
        }
      }
    }
  };
  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target?.files) {
      validateImageAndUpload(event.target.files?.[0]);
    }
  };
  const handleClick = () => {
    if (props.file || props.defaultFile) {
      return;
    }
    if (fileInputRef.current) {
      if (!props.fetchBlobKey) fileInputRef.current.value = "";
      if (!imageLoading) fileInputRef.current.click();
      if (props.setFile && props.file) props.setFile(null);
      if (
        !props.fetchBlobKey &&
        props.setFileAttributes &&
        props.fileAttributes
      )
        props.setFileAttributes({ fileName: "", fileType: "" });
    }
  };
  const handleCrossButton = () => {
    if (props.file) {
      if (props.setFile && props.file) props.setFile(null);
    }
    if (props.defaultFile && props.setDefaultFile) {
      props.setDefaultFile("");
    }
    if (props.setFileAttributes) {
      props.setFileAttributes({ fileName: "", fileType: "" });
    }
  };
  return (
    <>
      <div
        className={
          "generic-file-upload-container" +
          (props.className ? ` ${props.className}` : "")
        }
      >
        <div className="generic-file-upload" onClick={handleClick}>
          {imageLoading ? (
            <Loader />
          ) : props.file ? (
            <>
              <img
                src={imageDisplayIcon}
                height="60"
                width="auto"
                alt="banner preview"
              />
              <Typography variant="subtitle1">
                {fileName || props.fileAttributes?.fileName || ""}
              </Typography>
            </>
          ) : props.defaultFile ? (
            <>
              <img
                src={imageDisplayIcon}
                height="60"
                width="auto"
                ref={imgRef}
                onError={() => {
                  if (imgRef.current) imgRef.current.src = defaultFileIcon;
                }}
                alt="banner preview"
              />
              <Typography variant="subtitle1">
                {fileName || props.fileAttributes?.fileName || ""}
              </Typography>
            </>
          ) : (
            <>
              {props.accept.application?.find((e) => {
                return e === "pdf";
              }) ? (
                <img
                  src={pdfIconNew}
                  height="50"
                  width="auto"
                  alt="banner preview"
                />
              ) : (
                <FontAwesomeIcon fontSize="45px" icon={faCamera} />
              )}

              {props.resolution!.width === 0 &&
              props.resolution!.height === 0 ? (
                <Typography variant="subtitle1">
                  {translate(TranslationEnum.common_portal, `${props.label}`)}
                </Typography>
              ) : (
                <Typography variant="subtitle1">
                  {translate(
                    TranslationEnum.common_portal,
                    `${props.label} {width}X{height}`,
                    {
                      width: props.resolution!.width.toString(),
                      height: props.resolution!.height.toString(),
                    }
                  )}
                </Typography>
              )}
            </>
          )}
          <input
            type="file"
            onChange={handleImageUpload}
            hidden
            accept={acceptAttribute}
            ref={fileInputRef}
          />
        </div>
        {!imageLoading && (props.file || props.defaultFile) ? (
          <div className="generic-file-upload-cross-container">
            {/* <FontAwesomeIcon
                  className="generic-file-upload-cross"
                  icon={faXmark}
                  onClick={handleCrossButton}
              /> */}
            <RemoveButton onClick={handleCrossButton}>×</RemoveButton>
          </div>
        ) : (
          <></>
        )}
      </div>
      <Typography
        className="generic-file-upload-error-message"
        variant="subtitle2"
        color="red"
      >
        {props.errorMessage ?? ""}
      </Typography>
    </>
  );
};

export default GenericFileUpload;
